import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase, getSiteUrl } from "@/lib/supabase";

// Hard-coded list of admin user IDs
// In a production app, this would be stored in the database
const ADMIN_USER_IDS: string[] = [
  "8e62a312-e432-4f9e-b399-ca093abd5422" // Add the admin user IDs here
];

// Simple function to check if a user is an admin
const checkAdminStatus = (userId: string | undefined): boolean => {
  if (!userId) return false;
  
  // Log for debugging
  console.log(`Checking admin status for user: ${userId}`);
  console.log(`Admin IDs: ${JSON.stringify(ADMIN_USER_IDS)}`);
  
  // Check if user ID is in the admin list
  const isAdmin = ADMIN_USER_IDS.includes(userId);
  console.log(`Is user admin? ${isAdmin}`);
  
  return isAdmin;
};

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if there's a session in the URL hash (for OAuth redirects)
    const handleHashRedirect = async () => {
      if (window.location.hash && window.location.hash.includes('access_token')) {
        try {
          // Process the hash and establish the session
          const { data, error } = await supabase.auth.getSession();
          if (error) {
            console.error("Error processing auth redirect:", error);
          } else if (data.session) {
            setUser(data.session.user);
            setSession(data.session);
            console.log("Session established from redirect", { user: data.session.user.id });
          }
        } catch (err) {
          console.error("Error handling hash redirect:", err);
        }
      }
    };

    // Check active sessions and sets the user
    const initializeAuth = async () => {
      try {
        console.log("Initializing auth and checking for existing session");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          return;
        }
        
        if (data.session) {
          console.log("Found existing session", { 
            userId: data.session.user.id,
            expiresAt: new Date(data.session.expires_at! * 1000).toISOString()
          });
          setUser(data.session.user);
          setSession(data.session);
          
          // Check if user is an admin
          const adminStatus = checkAdminStatus(data.session.user.id);
          console.log(`Setting admin status to: ${adminStatus}`);
          setIsAdmin(adminStatus);
          
          // Refresh token if it's close to expiring (within 24 hours)
          const expiresIn = data.session.expires_at! - Math.floor(Date.now() / 1000);
          if (expiresIn < 60 * 60 * 24) {
            console.log("Session close to expiry, refreshing token");
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error("Error refreshing token:", refreshError);
            } else if (refreshData.session) {
              console.log("Token refreshed successfully");
              setUser(refreshData.session.user);
              setSession(refreshData.session);
            }
          }
        } else {
          console.log("No active session found");
          setUser(null);
          setSession(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Error in auth initialization:", error);
      } finally {
        setLoading(false);
      }
    };

    // Handle hash redirects first, then initialize auth
    handleHashRedirect().then(() => {
      if (!window.location.hash.includes('access_token')) {
        initializeAuth();
      } else {
        setLoading(false);
      }
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log("Auth state changed", { event: _event, userId: session?.user?.id });
      setUser(session?.user ?? null);
      setSession(session);
      
      // Update admin status when auth state changes
      if (session?.user) {
        // Check if user is an admin
        const adminStatus = checkAdminStatus(session.user.id);
        console.log(`Setting admin status to: ${adminStatus}`);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      console.log("Sign in successful", { userId: data.user?.id });
      return data;
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${getSiteUrl()}/auth/confirm`,
      },
    });

    if (error) throw error;

    // Check if user was created but needs verification
    if (data?.user?.identities?.length === 0) {
      throw new Error("Email already registered");
    }

    // Create initial profile
    if (data.user) {
      const { error: profileError } = await supabase.from("profiles").insert({
        id: data.user.id,
        display_name: email.split("@")[0], // Use email username as initial display name
        created_at: new Date().toISOString(),
      });

      if (profileError) {
        console.error("Error creating profile:", profileError);
        // Log error but don't throw to allow signup to complete
      }
    }

    return data;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log("Sign out successful");
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    console.log("Initiating password reset for:", email);
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getSiteUrl() + "/reset-password",
    });

    console.log("Reset password response:", { data, error });

    if (error) throw error;
    return data;
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, isAdmin, signIn, signUp, signOut, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
};
